import type { ProductResponse } from '@malaberg/shared';
/**
 * use-products hook — T23.
 *
 * Fetches the active products list from the API.
 * Returns { products, loading, error, refetch }.
 *
 * Products are fetched once on mount. Call refetch() to refresh.
 */
import { useCallback, useEffect, useState } from 'react';
import { productsApi } from '../api/endpoints/products';

export interface UseProductsResult {
  products: ProductResponse[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useProducts(): UseProductsResult {
  const [products, setProducts] = useState<ProductResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    productsApi
      .list({ limit: 200 })
      .then((res) => {
        if (cancelled) return;
        const data = res as { ok: boolean; items: ProductResponse[] };
        setProducts(data.items.filter((p) => p.active));
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load products');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { products, loading, error, refetch };
}
